import { Injectable } from '@angular/core';
import { ToastrService, ActiveToast } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';

export interface ToastOptions {
    title?: string;
    message?: string;
    type?: 'success' | 'error' | 'warning' | 'info';
    timeOut?: number;
    extendedTimeOut?: number;
    closeButton?: boolean;
    newestOnTop?: boolean;
    positionClass?: string;
    tapToDismiss?: boolean;
    enableHtml?: boolean;
    toastClass?: string;
}

@Injectable({
    providedIn: 'root'
})
export class ToasterService {

    constructor(
        private toastr: ToastrService,
        private translate: TranslateService
    ) { }

    /**
     * Build message with custom button container
     */
    private buildMessageWithButton(message: string): string {
        // Return HTML string with container - button will be added programmatically
        return `<div class="toast-message-content"><div class="toast-message-text">${message}</div></div>`;
    }

    /**
     * Show a success toast notification
     */
    success(message: string, title: string = 'Success', options: Partial<ToastOptions> = {}) {
        const messageWithButton = this.buildMessageWithButton(message);
        const toast = this.toastr.success(messageWithButton, title, {
            ...options
        });
        this.setupButtonClickHandler(toast);
        return toast;
    }

    /**
     * Show an error toast notification
     */
    error(message: string, title: string = 'Error', options: Partial<ToastOptions> = {}) {
        const messageWithButton = this.buildMessageWithButton(message);
        const toast = this.toastr.error(messageWithButton, title, {
            ...options
        });
        this.setupButtonClickHandler(toast);
        return toast;
    }

    /**
     * Show a warning toast notification
     */
    warning(message: string, title: string = 'Warning', options: Partial<ToastOptions> = {}) {
        const messageWithButton = this.buildMessageWithButton(message);
        const toast = this.toastr.warning(messageWithButton, title, {
            ...options
        });
        this.setupButtonClickHandler(toast);
        return toast;
    }

    /**
     * Show an info toast notification.
     * @param options.warnBg - when true, toast background uses $c-warn color instead of default
     */
    info(message: string, title?: string, options: Partial<ToastOptions> = {}, warn: boolean = false) {
        const toastOptions = {
            ...options
        };
        if (warn) {
            toastOptions.toastClass = 'toast-warn-bg';
        }
        const messageWithButton = this.buildMessageWithButton(message);
        const toast = this.toastr.info(messageWithButton, title, toastOptions);
        this.setupButtonClickHandler(toast);
        return toast;
    }

    /**
     * Show a custom toast notification
     */
    show(options: ToastOptions) {
        const { title, message, type = 'info', ...restOptions } = options;
        const messageWithButton = this.buildMessageWithButton(message || '');
        const toastOptions = {

            ...restOptions
        };

        let toast: ActiveToast<any>;
        switch (type) {
            case 'success':
                toast = this.toastr.success(messageWithButton, title || '', toastOptions);
                break;
            case 'error':
                toast = this.toastr.error(messageWithButton, title || '', toastOptions);
                break;
            case 'warning':
                toast = this.toastr.warning(messageWithButton, title || '', toastOptions);
                break;
            case 'info':
            default:
                toast = this.toastr.info(messageWithButton, title || '', toastOptions);
                break;
        }
        this.setupButtonClickHandler(toast);
        return toast;
    }

    /**
     * Setup click handler for the custom close button
     */
    private setupButtonClickHandler(toast: ActiveToast<any>) {
        // Try multiple times with increasing delays to ensure DOM is ready
        const tryCreateButton = (attempt: number = 0) => {
            const maxAttempts = 10;
            const delay = attempt * 100 + 50; // Start at 50ms, increment by 100ms

            setTimeout(() => {
                // Find the toast-message element that contains our content
                const allMessageElements = document.querySelectorAll('#toast-container .toast-message');
                let messageContent: HTMLElement | null = null;

                // Look for the one containing our toast-message-content div
                for (let i = allMessageElements.length - 1; i >= 0; i--) {
                    const msgEl = allMessageElements[i] as HTMLElement;
                    const content = msgEl.querySelector('.toast-message-content');
                    if (content && !content.querySelector('.toast-close-button')) {
                        messageContent = content as HTMLElement;
                        break;
                    }
                }

                if (messageContent) {
                    // Create button
                    const button = document.createElement('button');
                    button.className = 't-button-outline white';
                    button.setAttribute('data-toast-id', toast.toastId.toString());
                    button.setAttribute('type', 'button');
                    button.textContent = this.translate.instant('translate.general.toasterOk');

                    // Add click handler
                    button.addEventListener('click', (e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        this.toastr.clear(toast.toastId);
                    });

                    // Append button to content
                    messageContent.appendChild(button);
                } else if (attempt < maxAttempts) {
                    // Retry if not found yet
                    tryCreateButton(attempt + 1);
                }
            }, delay);
        };

        tryCreateButton();
    }
}

